import React, { useEffect, useMemo, useCallback } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import Select from "react-select";
import { z } from "zod";

import Button from "../../Components/Button";
import Modal from "../../Components/Modal";
import TextInput from "../../Components/Inputs/Text";
import Multiselect from "../../Components/Inputs/Multiselect";
import request from "../../Api/request";
import useForm from "../../Hooks/useForm";
import FormField from "./FormField";
import { ACCOUNT_TYPE_OPTIONS, ENVIRONMENT_OPTIONS, ROLE_OPTIONS, DEFAULT_FORM_VALUES } from "./constants";

import css from "./styles.styl";

// Zod schema for form validation
const formSchema = z.object({
    displayName: z.string().min(1, "Display name is required"),
    email: z.string().email("Please enter a valid email address"),
    accountType: z.string().min(1, "Account type is required"),
    environments: z.array(z.string()).min(1, "At least one environment must be selected"),
    roles: z.array(z.string()).min(1, "At least one role must be selected")
});

export const LoganUserModal = ({ isOpen, onClose }) => {
    const queryClient = useQueryClient();

    const { state, updateForm, validate, resetForm, setInitialState } = useForm({
        schema: formSchema,
        defaultValues: DEFAULT_FORM_VALUES
    });

    // Set initial form values when the modal opens
    useEffect(() => {
        if (isOpen) {
            setInitialState(DEFAULT_FORM_VALUES);
        }
    }, [isOpen]);


    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [resetForm, onClose]);

    const { mutate: createLoganUser, isPending, error } = useMutation({
        mutationFn: async (userData) => {
            const response = await request({
                url: `/administration/users`,
                method: "POST",
                data: userData,
            });
            
            if (!response.data.success) {
                throw new Error(response.data.errors?.[0] || "Failed to create user");
            }
            
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["logan-users"] });
            handleClose();
        },
        onError: (error) => {
            console.error("Error creating Logan user:", error);
        }
    });

    const handleAddLoganUser = () => {
        const validation = validate();

        if (!validation.isValid) {
            console.error("Form validation failed:", validation.errors);
            return;
        }

        createLoganUser({
            displayName: state.displayName,
            email: state.email,
            accountType: state.accountType,
            environments: state.environments,
            roles: state.roles
        });
    };

    const validation = validate();
    const selectedAccountType = useMemo(() =>
        ACCOUNT_TYPE_OPTIONS.find(option => option.value === state.accountType),
        [state.accountType]
    );

    const modalContent = (
        <div className={css.modalContent}>
            <div className={css.formContainer}>
                <h3 className={css.modalTitle}>Add Logan User</h3>

                <FormField
                    label="Name"
                    error={validation.errors?.displayName?.message}
                >
                    <TextInput
                        value={state.displayName}
                        onChange={(value) => updateForm({ displayName: value })}
                        placeholder="Enter full name"
                    />
                </FormField>

                <FormField
                    label="Email"
                    error={validation.errors?.email?.message}
                >
                    <TextInput
                        type="email"
                        value={state.email}
                        onChange={(value) => updateForm({ email: value })}
                        placeholder="Enter email address"
                    />
                </FormField>

                <FormField
                    label="Account Type"
                    error={validation.errors?.accountType?.message}
                >
                    <Select
                        value={selectedAccountType}
                        onChange={(option) => updateForm({ accountType: option?.value || "" })}
                        options={ACCOUNT_TYPE_OPTIONS}
                        placeholder="Select account type"
                        isClearable
                    />
                </FormField>

                <FormField
                    label="Environments"
                    error={validation.errors?.environments?.message}
                >
                    <div className={css.scrollableContainer}>
                        <Multiselect
                            options={ENVIRONMENT_OPTIONS}
                            value={state.environments}
                            onChange={(value) => updateForm({ environments: value })}
                        />
                    </div>
                </FormField>

                <FormField
                    label="Roles"
                    error={validation.errors?.roles?.message}
                    isLast={true}
                >
                    <div className={css.scrollableContainer}>
                        <Multiselect
                            options={ROLE_OPTIONS}
                            value={state.roles}
                            onChange={(value) => updateForm({ roles: value })}
                        />
                    </div>
                </FormField>

                {error && (
                    <div className={css.submitError}>
                        {error.response?.data?.errors?.[0] || error.message || "Network error occurred"}
                    </div>
                )}

                <div className={css.actionButtons}>
                    <Button
                        color="grey"
                        onClick={handleClose}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="green"
                        onClick={handleAddLoganUser}
                        disabled={!validation.isValid || isPending}
                    >
                        {isPending ? "Creating..." : "Create Logan User"}
                    </Button>
                </div>
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <Modal
            size="large"
            content={modalContent}
            buttons={[]}
            handleClose={handleClose}
        />
    );
};
